import React, { useMemo } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import * as topojson from 'topojson-client';
import { geoAlbersUsa } from 'd3-geo';
import { COLORS } from '../utils/colors';
import useStore from '../store';
import { filterNodes } from '../graphLoader';
import usCountiesTopo from '../data/us-counties.topo.json';

function USMap() {
  const { graphData, filters } = useStore();
  
  // Calculate which counties have active bids
  const activeCounties = useMemo(() => {
    if (!graphData) return new Set();
    
    const hasActiveFilters = (
      filters.searchQuery ||
      filters.categories.length > 0 ||
      filters.agencies.length > 0 ||
      filters.minValue > 0 ||
      filters.maxValue < 100000000
    );
    
    if (!hasActiveFilters) return new Set();
    
    const filteredBids = filterNodes(graphData.nodes, filters);
    
    const counties = new Set();
    filteredBids.forEach(bid => {
      const countyId = getCountyFromLatLon(bid.lat, bid.lon);
      if (countyId) counties.add(countyId);
    });
    
    return counties;
  }, [graphData, filters]);
  
  // Convert TopoJSON to GeoJSON features
  const counties = useMemo(() => {
    const topoObjectKey = usCountiesTopo.objects.counties 
      ? 'counties' 
      : Object.keys(usCountiesTopo.objects)[0];
    
    const geojson = topojson.feature(
      usCountiesTopo,
      usCountiesTopo.objects[topoObjectKey]
    );
    
    const projection = geoAlbersUsa()
      .scale(1300)
      .translate([0, 0]);
    
    const countyMeshes = geojson.features.map((feature) => {
      const geoid = feature.properties.GEOID || feature.id;
      const isActive = activeCounties.has(geoid);
      
      const coordinates = feature.geometry.type === 'Polygon' 
        ? [feature.geometry.coordinates]
        : feature.geometry.coordinates;
      
      const shapes = coordinates.map(polygon => {
        const rings = polygon;
        
        return rings.map((ring) => {
          const points = ring
            .map(coord => {
              const projected = projection(coord);
              if (!projected) return null;
              return {
                x: projected[0] / 100,
                z: projected[1] / 100
              };
            })
            .filter(Boolean);
          
          if (points.length < 3) return null;
          
          const shape = new THREE.Shape();
          shape.moveTo(points[0].x, points[0].z);
          for (let i = 1; i < points.length; i++) {
            shape.lineTo(points[i].x, points[i].z);
          }
          shape.closePath();
          
          const linePoints = points.map(p => new THREE.Vector3(p.x, 0, p.z));
          linePoints.push(linePoints[0]);
          
          return { shape, linePoints };
        }).filter(Boolean);
      }).flat();
      
      if (shapes.length === 0) return null;
      
      return {
        id: geoid,
        name: feature.properties.NAME || 'Unknown',
        state: feature.properties.STATEFP,
        isActive,
        shapes
      };
    }).filter(Boolean);
    
    return countyMeshes;
  }, [activeCounties]);
  
  // NO ANIMATIONS - COMPLETELY STATIC
  return (
    <group>
      {counties.map((county) => (
        <CountyMesh key={county.id} county={county} />
      ))}
    </group>
  );
}

function getCountyFromLatLon(lat, lon) {
  const socal = {
    '06037': { minLat: 33.7, maxLat: 34.8, minLon: -118.7, maxLon: -118.0 },
    '06073': { minLat: 32.5, maxLat: 33.5, minLon: -117.6, maxLon: -116.1 },
    '06059': { minLat: 33.4, maxLat: 33.9, minLon: -118.0, maxLon: -117.4 },
  };
  
  for (const [fips, bounds] of Object.entries(socal)) {
    if (lat >= bounds.minLat && lat <= bounds.maxLat &&
        lon >= bounds.minLon && lon <= bounds.maxLon) {
      return fips;
    }
  }
  
  return null;
}

function CountyMesh({ county }) {
  // STATIC COLORS - NO ANIMATION
  const fillColor = county.isActive ? COLORS.cyan : COLORS.violet;
  const accentColor = COLORS.neonGreen;
  const lineColor = county.isActive ? COLORS.cyan : COLORS.textDimmer;
  const lineWidth = county.isActive ? 2.0 : 0.4;
  const lineOpacity = county.isActive ? 0.9 : 0.2;
  const fillOpacity = county.isActive ? 0.18 : 0.03;
  const accentOpacity = county.isActive ? 0.15 : 0;
  
  return (
    <group>
      {county.shapes.map((shapeData, idx) => (
        <React.Fragment key={`${county.id}-${idx}`}>
          {/* Base fill - STATIC */}
          <mesh
            rotation={[Math.PI / 2, 0, 0]}
            position={[0, -0.01, 0]}
          >
            <shapeGeometry args={[shapeData.shape]} />
            <meshBasicMaterial
              color={fillColor}
              transparent
              opacity={fillOpacity}
              side={THREE.DoubleSide}
            />
          </mesh>
          
          {/* Neon green accent - STATIC */}
          {county.isActive && idx === 0 && (
            <mesh
              rotation={[Math.PI / 2, 0, 0]}
              position={[0, -0.005, 0]}
            >
              <shapeGeometry args={[shapeData.shape]} />
              <meshBasicMaterial
                color={accentColor}
                transparent
                opacity={accentOpacity}
                side={THREE.DoubleSide}
                blending={THREE.AdditiveBlending}
              />
            </mesh>
          )}
          
          {/* County boundary - STATIC */}
          <Line
            points={shapeData.linePoints}
            color={lineColor}
            lineWidth={lineWidth}
            transparent
            opacity={lineOpacity}
          />
        </React.Fragment>
      ))}
    </group>
  );
}

export default USMap;